import { useId } from 'react';

import { ManagerProgressionTaskState } from '#src/__generated__/globalTypes.ts';
import { unitMapping, type Unit } from '#src/lib/style.ts';

type Props = {
  size?: Unit;
  status?: ManagerProgressionTaskState;
};

// Offset by 0.5px on both axes so the "assigned" stroke fits within the viewBox.
const FILL_PATH =
  'M6.97001 0.628425L1.00974 3.98108C0.330086 4.36338 0.330088 5.34192 1.00974 5.72423L6.97001 9.07688C7.27442 9.24811 7.64612 9.24811 7.95053 9.07688L13.9108 5.72423C14.5905 5.34192 14.5905 4.36338 13.9108 3.98108L7.95053 0.628424C7.64612 0.457192 7.27442 0.457192 6.97001 0.628425Z';

const STROKE_PATH =
  'M6.84699 0.41027C7.22751 0.196229 7.69303 0.196229 8.07355 0.41027L14.0335 3.76281C14.8831 4.24069 14.8831 5.46462 14.0335 5.9425L8.07355 9.29504C7.69303 9.50908 7.22751 9.50908 6.84699 9.29504L0.887029 5.9425C0.0374647 5.46462 0.0374633 4.24069 0.887029 3.76281L6.84699 0.41027Z';

const VIEWBOX_W = 14.9205;
const VIEWBOX_H = 9.7053;
const ASPECT_RATIO = `${VIEWBOX_W} / ${VIEWBOX_H}`;

export const StepIcon = ({
  size = 2,
  status = ManagerProgressionTaskState.ASSIGNED,
}: Props) => {
  const fillId = useId();
  const filterId = useId();

  return (
    <svg
      style={{
        height: unitMapping[size],
        aspectRatio: ASPECT_RATIO,
      }}
      viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      {status === ManagerProgressionTaskState.CLAIMED && (
        <>
          <g filter={`url(#${filterId})`}>
            <path d={FILL_PATH} fill={`url(#${fillId})`} />
          </g>
          <defs>
            <linearGradient
              id={fillId}
              x1="13.9445"
              y1="10.4776"
              x2="10.811"
              y2="3.0201"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FEF314" />
              <stop offset="1" stopColor="#FF4E22" />
            </linearGradient>
            <filter
              id={filterId}
              x="-50%"
              y="-80%"
              width="200%"
              height="260%"
              colorInterpolationFilters="sRGB"
            >
              <feGaussianBlur
                stdDeviation="1.5"
                in="SourceAlpha"
                result="blur"
              />
              <feFlood floodColor="#FF4E22" floodOpacity="0.5" result="color" />
              <feComposite in="color" in2="blur" operator="in" result="glow" />
              <feMerge>
                <feMergeNode in="glow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
        </>
      )}

      {status === ManagerProgressionTaskState.COMPLETED && (
        <>
          <g filter={`url(#${filterId})`}>
            <path d={FILL_PATH} fill="#00F3EB" />
          </g>
          <defs>
            <filter
              id={filterId}
              x="-50%"
              y="-80%"
              width="200%"
              height="260%"
              colorInterpolationFilters="sRGB"
            >
              <feGaussianBlur
                stdDeviation="1.5"
                in="SourceAlpha"
                result="blur"
              />
              <feFlood floodColor="#00F3EB" floodOpacity="0.5" result="color" />
              <feComposite in="color" in2="blur" operator="in" result="glow" />
              <feMerge>
                <feMergeNode in="glow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
        </>
      )}

      {status === ManagerProgressionTaskState.READY && (
        <path d={FILL_PATH} fill="white" fillOpacity={0.66} />
      )}

      {status !== ManagerProgressionTaskState.CLAIMED &&
        status !== ManagerProgressionTaskState.COMPLETED &&
        status !== ManagerProgressionTaskState.READY && (
          <>
            <path d={FILL_PATH} fill="#0E0E0E" fillOpacity={0.19} />
            <path
              d={STROKE_PATH}
              stroke="white"
              strokeOpacity={0.07}
              strokeWidth={0.5}
            />
          </>
        )}
    </svg>
  );
};
